import { Button, Drawer, Space } from "antd";
import { useCallback, useMemo, useState } from "react";
import { useAssetBatchesValueMappingContext } from "screens/assetExporter/feedConfigurationV2/shared/contexts/AssetBatchesValueMappingContext";
import { FiltersType } from "shared/components/TableFiltersSection";
import { useFetchTemplates } from "shared/hooks/useFetchTemplates";
import { videoCompositionEnabled } from "shared/constants/assetExporter";
import styles from "./TemplateDrawer.module.scss";
import Header from "./templateDrawer/Header";
import TemplateList from "./templateDrawer/TemplateList";
import { useAssetBatchesContext } from "../../../shared/contexts/AssetBatchesContext";
import {
  getFilterOptions,
  toDimensionStr,
} from "./templateDrawer/TemplateList.utils";

type TemplateDrawerProps = {
  open?: boolean;
  onClose?: () => void;
};

export const TemplateDrawer = ({ open, onClose }: TemplateDrawerProps) => {
  const [filters, setFilters] = useState<Partial<FiltersType>>({
    status: ["PUBLISHED"],
    stamps: [0],
  });

  const { templates: orgTemplates, isFetching } = useFetchTemplates({});
  const templates = useMemo(
    () =>
      orgTemplates.filter(
        template => template.type !== "html" || !!template.htmlFileUrl,
      ),
    [orgTemplates],
  );

  const filterOptions = useMemo(() => getFilterOptions(templates), [templates]);
  const {
    selectedTemplate,
    compositions,
    temporalDataFilters,
    setShowMediaAlert,
    setShowTextAlert,
    setTemplatesDataFilters,
    setTemporalDataFilters,
    setCompositionToReplace,
  } = useAssetBatchesContext();
  const { saveTemplate } = useAssetBatchesValueMappingContext();

  const setFiltersWithDataFilters = useCallback(() => {
    if (selectedTemplate && selectedTemplate.type !== "html") {
      setFilters({
        ...filters,
        type: filterOptions?.type?.filter(option => option !== "html"),
        dimension: [toDimensionStr(selectedTemplate) ?? ""],
      });
    }
  }, [filters, filterOptions, selectedTemplate]);

  const handleClose = useCallback(() => {
    if (videoCompositionEnabled) {
      if (temporalDataFilters) setTemplatesDataFilters(temporalDataFilters);
      setTemporalDataFilters(undefined);
      setCompositionToReplace(undefined);
      setFiltersWithDataFilters();
    }
    onClose?.();
  }, [
    temporalDataFilters,
    onClose,
    setFiltersWithDataFilters,
    setCompositionToReplace,
    setTemporalDataFilters,
    setTemplatesDataFilters,
  ]);

  return (
    <Drawer
      closable={false}
      destroyOnClose
      className={styles.drawer}
      placement="right"
      visible={open}
      width="calc(100vw - 48px)"
      bodyStyle={{ backgroundColor: "#f5f5f5", padding: 0 }}
      title={<Header />}
      footer={
        <Space className={styles.drawerFooter}>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            type="primary"
            onClick={() => {
              if (!selectedTemplate) return;
              setShowMediaAlert(true);
              setShowTextAlert(true);
              saveTemplate(selectedTemplate);

              if (
                selectedTemplate.type !== "html" &&
                videoCompositionEnabled &&
                compositions.length <= 1
              ) {
                setFiltersWithDataFilters();

                setTemplatesDataFilters({
                  type: filterOptions.type.filter(option => option !== "html"),
                  dimension: [toDimensionStr(selectedTemplate) ?? ""],
                });
              }

              onClose?.();
            }}
            data-cy="feed-next-btn"
            disabled={!selectedTemplate}
          >
            {"Add selected file"}
          </Button>
        </Space>
      }
    >
      <div style={{ width: "100%", height: "100%" }}>
        <TemplateList
          filters={filters}
          setFilters={setFilters}
          filterOptions={filterOptions}
          isFetching={isFetching}
          templates={templates}
        />
      </div>
    </Drawer>
  );
};
